import React from 'react';
import { useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import QuotationMarkIcon from '@atlaskit/icon/core/quotation-mark';
import PageIcon from '@atlaskit/icon/core/page';
import PageLiveDocIcon from '@atlaskit/icon-lab/core/page-live-doc';
import { Anchor } from '@atlaskit/primitives/compiled/anchor';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { token } from '@atlaskit/tokens';

import type { NestedAdfRendererWrapper } from '@confluence/macro-utils/entry-points/nestedAdfRenderer.ts';

import { ContentType } from '../contentByLabelTypes.ts';
import type { ContentByLabelItem as ContentItemData } from '../contentByLabelTypes.ts';
import { i18n } from '../contentByLabelMacroI18n.ts';
import { ExcerptType } from '../hooks/useParsedMacroParams.ts';

import { ContentExcerpt } from './ContentExcerpt.tsx';
import { LabelList } from './LabelList.tsx';

const styles = cssMap({
	listItem: {
		marginBlockStart: token('space.100'),
	},
	listItemFirst: {
		marginBlockStart: token('space.0'),
	},
	details: {
		paddingInlineStart: token('space.300'),
	},
	spaceInfo: {
		color: token('color.text.subtlest'),
	},
	contentTypeIcon: {
		display: 'flex',
		alignItems: 'center',
		flexShrink: 0,
	},
	titleLink: {
		textDecoration: 'none',
	},
});

interface ContentEntryProps {
	item: ContentItemData;
	showLabels: boolean;
	showSpace: boolean;
	excerptType: ExcerptType;
	limitLabelLinksToSpace: boolean;
	isFirst: boolean;
	RendererWrapper: NestedAdfRendererWrapper;
}

export const ContentEntry = function ContentEntry({
	item,
	showLabels,
	showSpace,
	excerptType,
	limitLabelLinksToSpace,
	isFirst,
	RendererWrapper,
}: ContentEntryProps): React.JSX.Element {
	const { formatMessage } = useIntl();

	const getContentTypeIcon = () => {
		if (item.type === ContentType.Blogpost) {
			return <QuotationMarkIcon label="" />;
		}
		if (item.type === ContentType.LivePage) {
			return <PageLiveDocIcon label="" />;
		}
		return <PageIcon label="" />;
	};
	const contentTypeIcon = getContentTypeIcon();

	return (
		<Box
			as="li"
			xcss={isFirst ? styles.listItemFirst : styles.listItem}
			testId={`content-entry-${item.id}`}
		>
			<Inline space="space.050" alignBlock="center" testId="content-by-label-inline">
				<Box xcss={styles.contentTypeIcon} testId={`content-type-icon-${item.type}`}>
					{contentTypeIcon}
				</Box>
				<Anchor href={item.url} xcss={styles.titleLink}>
					{item.title}
				</Anchor>
				{showSpace && (
					<Box as="span" xcss={styles.spaceInfo}>
						{formatMessage(i18n.spaceLabel, { spaceName: item.space.name })}
					</Box>
				)}
				{excerptType !== ExcerptType.None && (
					<Box as="span" xcss={styles.spaceInfo}>
						{formatMessage(i18n.excerptSeparator)}
					</Box>
				)}
			</Inline>

			<Box xcss={styles.details}>
				{excerptType !== ExcerptType.None && (
					<ContentExcerpt
						excerpt={item.excerpt}
						mediaToken={item.mediaToken}
						excerptType={excerptType}
						contentId={item.id}
						spaceKey={item.space.key}
						spaceId={item.space.id}
						RendererWrapper={RendererWrapper}
					/>
				)}

				{showLabels && item.labels.length > 0 && (
					<Box paddingBlockStart="space.050">
						<LabelList
							labels={item.labels}
							spaceKey={item.space.key}
							limitLabelLinksToSpace={limitLabelLinksToSpace}
						/>
					</Box>
				)}
			</Box>
		</Box>
	);
};
